import { Button } from "@mui/material";
import React, { useContext } from "react";

import DotElastic from "components/DotElastic";
import { FeatureTip } from "components/Payment/Plans";
import { InvoiceItem, Plan, Price } from "models/Payment";
import { Subscription } from "models/User";
import { formatPrice, getCurrencySymbol } from "utils/helper";

import styles from "./PlanSelected.module.scss";

interface PlanSelectedProps {
  plan: Plan;
  invoiceItems: InvoiceItem[] | null;
  submitText: string;
  onSubmit: () => void;
  disabled: boolean;
  subscription: Subscription;
}

const PlanSelected: React.FC<PlanSelectedProps> = ({
  plan,
  submitText,
  onSubmit,
  disabled,
  invoiceItems,
  subscription,
}) => {
  const total: Price | null = invoiceItems?.[0]
    ? {
        currency: invoiceItems[0].price.currency,
        unit_amount_decimal: invoiceItems.reduce(
          (sum, item) => sum + item.price.unit_amount_decimal,
          0,
        ),
      }
    : null;
  return (
    <div className={styles.container}>
      {invoiceItems && total ? (
        <>
          <div className={styles.header}>Summary</div>

          <div className={styles.body}>
            <div className={styles.planName}>{`${plan.name} Plan`}</div>

            <div>
              <div className={styles.billingCycle}>Billing cycle:</div>
              <div className={styles.monthly}>Monthly</div>
            </div>

            <div className={styles.invoiceItems}>
              {invoiceItems.map((item) => (
                <div key={item.item} className={styles.invoiceItem}>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                  >
                    <span>{item.item}</span>
                    {item.item.startsWith("Overages") && (
                      <FeatureTip
                        text={`Extra charges for frames exported over ${subscription.plan.frames} quota`}
                      />
                    )}
                    {item.item.startsWith("Autopilot") && (
                      <FeatureTip
                        text={`Extra charges for autopilot frames exported over ${subscription.plan.autopilot_frames} quota`}
                      />
                    )}
                  </div>
                  <div>{formatPrice(item.price, 3)}</div>
                </div>
              ))}
            </div>

            <div className={styles.subtotal}>
              <div>Subtotal</div>
              <div>{formatPrice(total)}</div>
            </div>

            <div className={styles.billedNow}>
              <div>Billed Now</div>
              <div>{formatPrice(total)}</div>
            </div>

            <Button
              disabled={disabled}
              className={styles.slapShotButton}
              onClick={onSubmit}
            >
              {submitText}
            </Button>
            <div className={styles.disclaimer}>
              <p>
                All sales are charged in {plan.price.currency.toUpperCase()} and
                all sales are final.
              </p>
              <br />
              <p>
                You will be charged {formatPrice(total)} immediately and{" "}
                {formatPrice(plan.price)} monthly hereafter while the
                subscription is active. Cancel anytime.
              </p>
            </div>
          </div>
        </>
      ) : (
        <div className={styles.loader}>
          <DotElastic />
        </div>
      )}
    </div>
  );
};

export default PlanSelected;
